import { useEffect } from 'react';
import { Check, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';
import { cn } from '@/shared/lib/cn';

type StatusKind = 'success' | 'error';

type Props = {
  open: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  /** Milliseconds until auto-dismiss. Success defaults to 2500, errors stay open. */
  autoCloseMs?: number;
};

export function StatusDialog({
  kind,
  open,
  onClose,
  title,
  message,
  autoCloseMs,
}: Props & { kind: StatusKind }) {
  const isSuccess = kind === 'success';

  // Errors do not auto-dismiss by default — users should read and acknowledge.
  const effectiveAutoCloseMs = autoCloseMs ?? (isSuccess ? 2500 : undefined);

  useEffect(() => {
    if (!open || !effectiveAutoCloseMs) return;
    const timer = window.setTimeout(onClose, effectiveAutoCloseMs);
    return () => window.clearTimeout(timer);
  }, [open, effectiveAutoCloseMs, onClose]);

  const resolvedTitle = title ?? (isSuccess ? '¡Listo!' : 'No se pudo completar la operación');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader className="items-center gap-3 text-center">
          <div
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-full',
              isSuccess
                ? 'bg-[hsl(var(--brand-green))]/10 text-[hsl(var(--brand-green))]'
                : 'bg-destructive/10 text-destructive',
            )}
            aria-hidden
          >
            {isSuccess ? <Check className="h-7 w-7" /> : <XCircle className="h-7 w-7" />}
          </div>
          <DialogTitle>{resolvedTitle}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" className="w-full sm:w-auto" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const SuccessDialog = (props: Props) => <StatusDialog kind="success" {...props} />;
export const ErrorDialog = (props: Props) => <StatusDialog kind="error" {...props} />;
